import React, { useEffect, useState } from "react";
import _ from "lodash";
import ReactSpinner from "react-spinjs-fix";
import { ButtonGroup } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";

import DocsItem from "../../docs/DocsItem";
import Show from "../../../../widgets/Show";
import Back from "../../../../widgets/Back";
import FlexBox from "../../../../ui/FlexBox";
import { useQuery } from "../../../../../hooks/useQuery";
import {
  getFolderList,
  openFolder,
  moveItems,
  uploadFiles,
  openImage,
  selectItem,
  closeFolder,
  loadFilesList,
  selectFile,
  resetFolder,
  imageTypeQuestionToggle,
  updatePatientDocsItemName,
  patientDocsTabChangeHandler,
} from "../../../../../actions/folderActions";
import { Modal } from "../../../../ui/Modal";
import uploadImage from "../../../../../assets/images/upload-icon.png";
import { dataToFillHandler } from "../../../../../actions/chartSideMedicalFillInFormActions";

const FormPatientDocs = ({
  form,
  setForm,
  questionId,
  editMode,
  visible,
  setOverlay,
  questionTypeCode,
  questionTypeCodes,
}) => {
  const query = useQuery();
  const dispatch = useDispatch();
  const isImage = questionTypeCode === questionTypeCodes.IMAGE;
  const folder = useSelector((store) => store.folder);

  const [files, setFiles] = useState([]);
  const [value, setValue] = useState("");
  const [fileName, setFileName] = useState("");
  const [show, setShow] = useState(visible || false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameFileValue, setRenameFileValue] = useState({});
  const item = form.questions.find((item) => item.id === questionId);

  useEffect(() => {
    if (item?.id) {
      setValue(item?.answerFile || item?.answerPhoto?.url || item?.answerPhoto?.url);
    }
  }, [item]);

  useEffect(() => {
    const { patientKey } = query;
    dispatch(
      getFolderList({
        patientKey,
        folderId: _.last(folder.folderStack) || null,
      }),
    );
    return () => {
      dispatch(resetFolder());
    };
  }, []);

  const tabHandler = (tab) => {
    const { patientKey } = query;
    const params = {
      patientKey,
      folderId: _.last(folder.folderStack) || null,
    };
    dispatch(patientDocsTabChangeHandler(tab, params));
  };

  if (!show) {
    return (
      <img
        id="mouthId"
        alt="mouth"
        className="medical-fill-in-form-answer"
        src={value}
        onClick={() => setShow(editMode)}
      />
    );
  }

  const currentFolder = _.last(folder.folderStack) || 0;
  const folderList = folder.folderList[currentFolder] || [];
  const filesList = folder.filesList[currentFolder] || [];

  let list = [];

  if (folder.isMoving) {
    list = folderList.filter((x) => !_.includes(folder.selectedFolders, x.id));
  } else {
    list = [...folderList, ...filesList];
  }

  const loadList = (event) => {
    const { patientKey } = query;
    const scrollBottom =
      event.target.scrollTop + event.target.offsetHeight === event.target.scrollHeight;

    if (scrollBottom) {
      dispatch(
        loadFilesList({
          patientKey,
          folderId: _.last(folder.folderStack) || null,
        }),
      );
    }
  };

  const backHandler = () => {
    setOverlay({ visible: false });
    setShow(false);
    dispatch(imageTypeQuestionToggle(false));
  };

  const openFileHandler = (id) => {
    dispatch(openImage(id));
  };

  const onSelectMoveFolder = (targetFolderId, isClosed) => {
    dispatch(
      moveItems(
        {
          patientKey: query.patientKey,
          targetFolderId,
        },
        isClosed,
      ),
    );
  };

  const onUpload = () => {
    let today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();

    today = dd + "-" + mm + "-" + yyyy;
    const newFiles = [];
    [...files].forEach((originalFile) => {
      const fileExtension = originalFile.name.slice(
        (Math.max(0, originalFile.name.lastIndexOf(".")) || Infinity) + 1,
      );
      const newFile = new File(
        [originalFile],
        today === fileName ? originalFile.name : fileName + "." + fileExtension,
        {
          type: originalFile.type,
          lastModified: originalFile.lastModified,
        },
      );
      newFiles.push(newFile);
    });
    dispatch(
      uploadFiles({
        patientKey: query.patientKey,
        files: newFiles,
        folderId: _.last(folder.folderStack) || null,
      }),
    );
    setFiles([]);
    setFileName("");
    setShowEditNameModal(false);
  };

  const renameFiles = (files) => {
    let _today = new Date();
    const dd = String(_today.getDate()).padStart(2, "0");
    const mm = String(_today.getMonth() + 1).padStart(2, "0");
    const yyyy = _today.getFullYear();

    _today = dd + "-" + mm + "-" + yyyy;
    setShowEditNameModal(true);
    setFiles(files);
    setFileName(_today);
  };

  const saveHandler = () => {
    setShow(false);
    setOverlay({ visible: false });
    dispatch(imageTypeQuestionToggle(false));
    const { selectedFile } = folder;

    setForm({
      ...form,
      questions: form.questions.map((question) => {
        if (question.id === questionId) {
          question.answerFile = selectedFile.url;
          question.answerPhoto = {
            id: selectedFile.id,
            key: selectedFile.key,
            url: selectedFile.url,
          };
          dispatch(dataToFillHandler(question.answerPhoto));
        }
        return question;
      }),
    });
  };

  const splitFileName = (filename) => {
    const mixtureMatch = filename.match(/\.(tar\.gz|tar\.bz2|min\.js)$/i);
    if (mixtureMatch) {
      const extension = mixtureMatch[1];
      const nameWithoutExt = filename.slice(0, mixtureMatch.index);
      return { name: nameWithoutExt, extension: extension };
    }

    const match = filename.match(/\.([^./]+)$/);
    if (match) {
      const extension = match[0];
      const nameWithoutExt = filename.slice(0, match.index);
      return { name: nameWithoutExt, extension: extension };
    } else {
      return { name: filename, extension: null };
    }
  };

  const renameSelectedItem = () => {
    const fileName = _.get(folder, "selectedFileToRename", "");
    const result = fileName?.type === "folder" ? fileName : splitFileName(fileName.name);

    setRenameFileValue(result);
    setShowRenameModal(true);
  };

  const onHideModal = () => {
    setShowRenameModal(false);
    setRenameFileValue({ name: "", extension: null });
  };

  const onSaveRenamedFile = () => {
    const { patientKey } = query;
    dispatch(updatePatientDocsItemName(renameFileValue, patientKey));
    setRenameFileValue({});
    setShowRenameModal(false);
  };

  return (
    <>
      <Show if={showEditNameModal}>
        <Modal
          title="Edit file name"
          show={true}
          actions={
            <div>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  setShowEditNameModal(false);
                  setFileName("");
                }}
              >
                cancel
              </button>
              <button type="button" className="btn btn-default" onClick={() => onUpload()}>
                ok
              </button>
            </div>
          }
        >
          <input
            className="form-control input-sm mb-3"
            type="text"
            value={fileName}
            onChange={({ target: { value } }) => setFileName(value)}
          />
        </Modal>
      </Show>
      <div className="chart-clinical-note" style={{ height: "100%" }}>
        <div
          className="sidebar-header chart-side-header"
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div className="w-100 d-flex align-items-center justify-content-between">
            <div
              style={{
                flex: 1,
                display: "flex",
              }}
            >
              {currentFolder > 0 ? (
                <DocsItem
                  isBack={true}
                  item={{
                    type: "folder",
                  }}
                  openFolder={() =>
                    folder.isMoving
                      ? onSelectMoveFolder(
                          folder.folderStack.length >= 2
                            ? folder.folderStack[folder.folderStack.length - 2]
                            : null,
                          true,
                        )
                      : dispatch(closeFolder({ patientKey: query.patientKey }))
                  }
                />
              ) : (
                <Back onClick={backHandler} />
              )}
            </div>

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                cursor: "pointer",
              }}
            >
              <div
                title="Upload"
                className="clinic-note patient-note"
                style={{
                  cursor: "pointer",
                  marginRight: "12px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <img alt="" src={uploadImage} width={32} height={32} />
                <input
                  style={{
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0,
                    width: "32px",
                    height: "32px",
                    cursor: "pointer",
                    position: "absolute",
                  }}
                  type="file"
                  tabIndex={-1}
                  multiple={true}
                  name="uploadFile"
                  onChange={({ target: { files } }) => renameFiles(files)}
                />
              </div>
            </div>
          </div>

          <Show if={folder.fetchingFolders}>
            <ReactSpinner />
          </Show>

          <div className="w-100" style={{ marginTop: "15px", padding: "0 5px" }}>
            <ButtonGroup aria-label="note tabs" className="d-flex align-items-center">
              <button
                type="button"
                onClick={() => tabHandler("activeDocs")}
                className={`btn ${
                  folder.docsActiveTab !== "activeDocs" ? "btn-primary" : "btn-default"
                } btn-block m-0`}
              >
                Documents
              </button>
              <button
                type="button"
                onClick={() => tabHandler("archived")}
                className={`btn ${
                  folder.docsActiveTab !== "archived" ? "btn-primary" : "btn-default"
                } btn-block m-0`}
              >
                Voided
              </button>
            </ButtonGroup>
          </div>
        </div>
        <div className="chart-side-body" onScroll={loadList}>
          {list.map((x, idx) => (
            <DocsItem
              item={x}
              key={idx}
              isImage={isImage}
              openFile={() => openFileHandler(x.id)}
              isMoving={folder.isMoving}
              openFolder={() =>
                folder.isMoving
                  ? onSelectMoveFolder(x.id)
                  : dispatch(
                      openFolder({
                        folderId: x.id,
                        patientKey: query.patientKey,
                      }),
                    )
              }
              onSelect={() => {
                dispatch(selectItem(x.type, x.id));
                dispatch(selectFile(x));
              }}
              selected={_.includes(
                x?.type === "folder" ? folder.selectedFolders : folder.selectedFiles,
                x.id,
              )}
              folder={folder}
            />
          ))}
        </div>
        {Boolean(!_.isEmpty(folder.selectedFiles) || !_.isEmpty(folder.selectedFolders)) && (
          <FlexBox container={16}>
            <FlexBox flex={true} direction="row" gutter={8}>
              <FlexBox flex={true}>
                <button
                  style={{ width: "100%" }}
                  className="btn btn-success btn-lg"
                  onClick={backHandler}
                >
                  Cancel
                </button>
              </FlexBox>

              <FlexBox flex={true}>
                <button
                  style={{ width: "100%" }}
                  className="btn btn-success btn-lg"
                  onClick={saveHandler}
                >
                  Save
                </button>
              </FlexBox>
              <FlexBox flex={true}>
                <button
                  style={{ width: "100%" }}
                  className="btn btn-success btn-lg"
                  onClick={renameSelectedItem}
                  disabled={[...folder.selectedFiles, ...folder.selectedFolders].length > 1}
                  title="Select only one item to rename !"
                >
                  Rename
                </button>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        )}
      </div>
      <Show if={showRenameModal}>
        <Modal
          title="Edit file name"
          show={true}
          onHide={onHideModal}
          actions={
            <div>
              <button type="button" className="btn btn-danger" onClick={onHideModal}>
                cancel
              </button>
              <button type="button" className="btn btn-default" onClick={onSaveRenamedFile}>
                ok
              </button>
            </div>
          }
        >
          <input
            className="form-control input-sm mb-3"
            type="text"
            value={renameFileValue.name}
            onChange={({ target: { value } }) =>
              setRenameFileValue({ ...renameFileValue, name: value })
            }
          />
        </Modal>
      </Show>
    </>
  );
};

export default FormPatientDocs;
